import { useCallback, useEffect, useRef, useState } from "react";

// Generic "Load More" pagination over a fetchPage(page) -> { items, hasMore }
// function. Re-runs from page 1 whenever `deps` changes. Copied verbatim from
// denzo-waiter's hooks/usePaginatedList.js — same shape of paginated response
// (pos/v1/orders/get-orders), same need to reset on a changed query.
export default function usePaginatedList(fetchPage, deps = [], initialCursor = 1) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(initialCursor);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });

  const loadPage = useCallback(async (thisCursor, append) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError("");
    try {
      const { items: newItems, hasMore: more, nextCursor } = await fetchPageRef.current(thisCursor);
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(more);
      setCursor(nextCursor !== undefined ? nextCursor : thisCursor + 1);
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(initialCursor, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) loadPage(cursor, true);
  };

  const reload = () => loadPage(initialCursor, false);

  return { items, loading, loadingMore, hasMore, error, loadMore, reload };
}
